'use client';

import { memo, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
} from '@/components/ui/breadcrumb';
import {
  Bell,
  Search,
  Filter,
  Download,
  RefreshCw,
  MoreHorizontal,
  Cloud,
  ChevronDown,
} from 'lucide-react';
import { useCloudProviderReadonly, useCloudProviderActions } from '@/hooks/useCloudProvider';
import { useProviderResourceCounts } from '@/hooks/useResourceCounts';
import type { CloudProvider } from '@/types/cloudProvider';

interface DashboardHeaderProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onRefresh: () => void;
  onExport: () => void;
  isRefreshing: boolean;
}

// Recent service type definition
interface RecentService {
  id: string;
  name: string;
  provider: string;
  icon: any; // Replace with proper icon type
}

// Sample recent services data
const defaultRecentServices: RecentService[] = [
  { id: '1', name: 'EC2 Instances', provider: 'AWS', icon: Search },
  { id: '2', name: 'S3 Buckets', provider: 'AWS', icon: Search },
  { id: '3', name: 'Cloud Functions', provider: 'GCP', icon: Search },
  { id: '4', name: 'Azure VMs', provider: 'Azure', icon: Search },
  { id: '5', name: 'Lambda Functions', provider: 'AWS', icon: Search },
];

// Phase 6b: Enhanced dynamic resource counting system
export const DashboardHeader = memo(
  ({
    searchQuery,
    onSearchChange,
    onRefresh,
    onExport,
    isRefreshing,
  }: DashboardHeaderProps) => {
    // Phase 6a: Direct Zustand store consumption
    const { provider, config, displayName } = useCloudProviderReadonly();
    const { setProvider } = useCloudProviderActions();
    const { counts, isLoading } = useProviderResourceCounts();
    
    // Recent services state
    const [recentServices, setRecentServices] = useState<RecentService[]>(defaultRecentServices);

    // Phase 6b: Define available providers with real-time resource counts
    const cloudProviders = [
      { id: 'aws', name: 'AWS', icon: '🟠', resourceCount: counts.aws },
      { id: 'gcp', name: 'Google Cloud', icon: '🔵', resourceCount: counts.gcp },
      { id: 'azure', name: 'Azure', icon: '🔷', resourceCount: counts.azure },
      { id: 'all', name: 'All Providers', icon: '☁️', resourceCount: counts.all },
    ];

    // Phase 6b: Dynamic provider configuration with loading state
    const currentProvider = {
      id: provider,
      name: displayName,
      icon: config.icon,
      resourceCount: isLoading ? '...' : counts[provider] || 0
    };

    const handleProviderChange = (selectedProvider: { id: string }) => {
      setProvider(selectedProvider.id as CloudProvider);
    };

    return (
      <header className="dashboard-header bg-background/95 sticky top-0 z-50 flex h-16 w-full shrink-0 items-center gap-2 border-b backdrop-blur transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
        <div className="flex items-center gap-2 px-4">
          <SidebarTrigger className="sidebar-trigger -ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem className="hidden md:block">
                <BreadcrumbLink href="#" className="breadcrumb-link">Home</BreadcrumbLink>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        <div className="ml-auto flex items-center gap-2 px-4">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-2"
          >
            {/* Cloud Provider Selection - Show on Desktop */}
            <div className="hidden items-center gap-2 lg:flex">
              <span className="text-sm text-muted-foreground">Provider:</span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="provider-dropdown-trigger gap-2">
                    <span>{currentProvider.icon}</span>
                    <span className="max-w-[120px] truncate">{currentProvider.name}</span>
                    <span className="text-muted-foreground">({currentProvider.resourceCount})</span>
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="dropdown-content w-48">
                  {cloudProviders.map((provider) => (
                    <DropdownMenuItem
                      key={provider.id}
                      onClick={() => handleProviderChange(provider)}
                      className="dropdown-menu-item flex items-center gap-2"
                    >
                      <span>{provider.icon}</span>
                      <span className="flex-1">{provider.name}</span>
                      <span className="text-muted-foreground text-xs">
                        {provider.resourceCount}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Search Input with Recent Services - Hide on Mobile */}
            <div className="relative hidden md:block">
              <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform" />
              <Popover>
                <PopoverTrigger asChild>
                  <div>
                    <Input
                      placeholder="Search resources..."
                      value={searchQuery}
                      onChange={(e) => onSearchChange(e.target.value)}
                      className="header-search-input w-64 pl-10"
                    />
                  </div>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-0" align="start">
                  <div className="p-2">
                    <p className="text-sm font-medium text-muted-foreground px-2 py-1">Recently used Services</p>
                    <div className="mt-2 space-y-1">
                      {recentServices.map((service) => (
                        <button
                          key={service.id}
                          onClick={() => {
                            onSearchChange(service.name);
                            setRecentServices(prev => 
                              [service, ...prev.filter(s => s.id !== service.id)].slice(0, 5)
                            );
                          }}
                          className="w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent rounded-sm"
                        >
                          <service.icon className="h-4 w-4 text-muted-foreground" />
                          <span className="flex-1 truncate text-left">{service.name}</span>
                          <span className="text-xs text-muted-foreground">{service.provider}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Desktop Actions */}
            <div className="hidden items-center gap-2 md:flex">
              <Button variant="outline" size="sm" className="header-action-button">
                <Filter className="mr-2 h-4 w-4" />
                Filter
              </Button>

              <Button variant="outline" size="sm" onClick={onExport} className="header-action-button">
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={onRefresh}
                disabled={isRefreshing}
                className="header-action-button"
              >
                <RefreshCw
                  className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`}
                />
                Refresh
              </Button>
            </div>

            {/* Mobile Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild className="md:hidden">
                <Button variant="outline" size="icon" className="header-action-button">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="dropdown-content w-48">
                <DropdownMenuItem onClick={() => onSearchChange('')} className="dropdown-menu-item">
                  <Search className="mr-2 h-4 w-4" />
                  Search
                </DropdownMenuItem>
                <DropdownMenuItem className="dropdown-menu-item">
                  <Cloud className="mr-2 h-4 w-4" />
                  Provider: {currentProvider.name}
                </DropdownMenuItem>
                <DropdownMenuItem className="dropdown-menu-item">
                  <Filter className="mr-2 h-4 w-4" />
                  Filter
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onExport} className="dropdown-menu-item">
                  <Download className="mr-2 h-4 w-4" />
                  Export
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onRefresh} className="dropdown-menu-item">
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Refresh
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button variant="outline" size="sm" className="notification-bell">
              <Bell className="h-4 w-4" />
            </Button>
          </motion.div>
        </div>
      </header>
    );
  },
);

DashboardHeader.displayName = 'DashboardHeader';
